'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useScrollTrigger,
  Slide,
  // Divider,
} from '@mui/material';
import { styled, useTheme as useMuiTheme, Theme } from '@mui/material/styles';
import {
  Menu,
  Home,
  BookText,
  Brain,
  Network,
  FileCode,
  Github,
  X,
  Library,
  Code2,
} from 'lucide-react';

interface NavItem {
  href: string;
  label: string;
  icon?: React.ElementType;
  description?: string;
}

interface NavigationProps {
  items: NavItem[];
  containerClassName?: string;
  linkClassName?: string;
}

const iconMap: Record<string, React.ElementType> = {
  Home: Home,
  MyBlog: BookText,
  AboutMe: Brain,
  MyVenture: Network,
  StoryMode: Library,
  Network: Network,
  NetGallery: Code2,
};

const useTheme = (): Theme => useMuiTheme() as Theme;

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  ...(theme as any).custom.mixins.glassmorphism,
  transition: (theme as any).custom.transitions.medium,
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  padding: theme.spacing(1.5, 2),
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    width: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #60a5fa, #a855f7)',
    transition: theme.custom.transitions.medium,
    transform: 'translateX(-50%)',
  },
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    '&::after': {
      width: '80%',
    },
  },
  '&.active': {
    color: theme.palette.primary.main,
    '&::after': {
      width: '80%',
    },
  },
}));

const BrandTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.h5.fontSize,
  fontWeight: 600,
  background: 'linear-gradient(90deg, #60a5fa, #a855f7)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  letterSpacing: '1px',
  userSelect: 'none',
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    ...theme.custom.mixins.glassmorphism,
    width: 280,
    backdropFilter: theme.custom.gradients.blur.medium,
    WebkitBackdropFilter: theme.custom.gradients.blur.medium,
  },
}));

const Navigation: React.FC<NavigationProps> = ({ 
  items, 
  containerClassName = '',
  linkClassName = '', 
}) => {
  const theme = useTheme();
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 20,
  });

  const isActiveLink = (href: string) => mounted && pathname === href;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const enhancedItems: NavItem[] = items.map((item) => ({
    ...item,
    icon: iconMap[item.label] || FileCode,
  }));

  const drawer = (
    <Box sx={{ pt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Github size={24} />
        <BrandTypography sx={{ ml: 2 }}>Cdaprod</BrandTypography>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            ml: 'auto',
            color: 'white',
            '&:hover': { background: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          <X size={24} />
        </IconButton>
      </Box>
      <List>
        {enhancedItems.map((item) => (
          <Link key={item.href} href={item.href} passHref>
            <ListItem
              button
              onClick={handleDrawerToggle}
              className={linkClassName}
              sx={{
                py: 2,
                color: isActiveLink(item.href)
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActiveLink(item.href)
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                  minWidth: 40,
                }}
              >
                {item.icon && <item.icon size={20} />}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.description}
                primaryTypographyProps={{ fontWeight: 500 }}
              />
            </ListItem>
          </Link>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <Slide appear={false} direction="down" in={!trigger}>
        <StyledAppBar
          position="fixed"
          sx={{
            py: trigger ? 0.5 : 1,
            backgroundColor: trigger
              ? 'rgba(0, 0, 0, 0.7)'
              : theme.palette.background.glass,
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  mr: 2,
                  display: { sm: 'none' },
                }}
              >
                <Menu size={24} />
              </IconButton>
              <Link href="/" passHref>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <Github
                    size={24}
                    style={{
                      marginRight: theme.spacing(2),
                      color: theme.palette.text.primary,
                    }}
                  />
                  <BrandTypography>Cdaprod</BrandTypography>
                </Box>
              </Link>
            </Box>

            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              {enhancedItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  passHref
                  style={{ textDecoration: 'none' }}
                >
                  <NavButton
                    className={`${linkClassName} ${isActiveLink(item.href) ? 'active' : ''}`}
                    startIcon={item.icon && <item.icon size={18} />}
                  >
                    {item.label}
                  </NavButton>
                </Link>
              ))}
            </Box>
          </Toolbar>
        </StyledAppBar>
      </Slide>
      <Box
        component="nav"
        className={containerClassName}
        sx={{ position: 'relative', zIndex: theme.zIndex.appBar }}
      >
        <StyledDrawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
          }}
        >
          {drawer}
        </StyledDrawer>
      </Box>
      <Box sx={{ height: { xs: 56, sm: 64 } }} />
    </>
  );
};

export default Navigation;